import * as React from "react";

import { cn } from "../../lib/utils";

/** Numeric read-out — replaces every gradient stat card and stat tile. */

export interface MetricProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onClick" | "children"
> {
  label: string;
  value: string | number;
  /** Period-over-period change. */
  delta?: { value: string; direction: "up" | "down" | "flat" };
  /** Small tertiary caption under the value. */
  hint?: string;
  /** Leading glyph next to the label. */
  icon?: React.ReactNode;
  /**
   * Semantic colour for the value and its icon. `"default"` is the neutral
   * text tone every metric had before; the others mark a figure the viewer has
   * to act on (e.g. Overdue) so it reads as more than one number among four.
   */
  tone?: "default" | "danger" | "warning" | "success";
  /** Turns the metric into a filter toggle. */
  onClick?: () => void;
  selected?: boolean;
}

const deltaTone = {
  up: "text-success",
  down: "text-danger",
  flat: "text-text-tertiary",
} as const;

const deltaGlyph = {
  up: "↑",
  down: "↓",
  flat: "→",
} as const;

const valueTone = {
  default: "text-text",
  danger: "text-danger",
  warning: "text-warning",
  success: "text-success",
} as const;

const labelTone = {
  default: "text-text-tertiary",
  danger: "text-danger/80",
  warning: "text-warning/80",
  success: "text-success/80",
} as const;

const Metric = React.forwardRef<HTMLDivElement, MetricProps>(
  (
    {
      label,
      value,
      delta,
      hint,
      icon,
      tone = "default",
      onClick,
      selected = false,
      className,
      ...props
    },
    ref,
  ) => {
    const interactive = Boolean(onClick);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onClick();
      },
      [onClick],
    );

    return (
      <div
        ref={ref}
        data-slot="metric"
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-pressed={interactive ? selected : undefined}
        onClick={onClick}
        onKeyDown={interactive ? handleKeyDown : undefined}
        className={cn(
          "flex flex-col gap-0.5 rounded-md px-4 py-2",
          "transition-[background-color] duration-100 ease-out-fast",
          interactive && "cursor-pointer hover:bg-hover",
          interactive && "focus-visible:outline-none focus-fill",
          selected && "bg-selected",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "flex items-center gap-1.5 text-xs font-medium",
            "[&_svg]:size-3.5 [&_svg]:shrink-0",
            labelTone[tone],
          )}
        >
          {icon}
          <span className="truncate">{label}</span>
        </span>
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(
              "text-2xl font-semibold tabular-nums tracking-tighter",
              valueTone[tone],
            )}
          >
            {value}
          </span>
          {delta ? (
            <span
              className={cn(
                "text-xs font-medium tabular-nums",
                deltaTone[delta.direction],
              )}
            >
              <span aria-hidden="true">{deltaGlyph[delta.direction]}</span>
              {delta.value}
            </span>
          ) : null}
        </span>
        {hint ? (
          <span className="truncate text-xs text-text-tertiary">{hint}</span>
        ) : null}
      </div>
    );
  },
);
Metric.displayName = "Metric";

export type MetricRowProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Container for a row of `Metric`s — no cards, no gradients. Borderless: the
 * `divide-x`/`border-y` rules are replaced by `gap` on this flex parent
 * (`docs/borderless-contract.md` §1). Each `Metric` keeps its own `rounded-md`
 * hover well, so the gap is what reads as the separator.
 */
const MetricRow = React.forwardRef<HTMLDivElement, MetricRowProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="metric-row"
      className={cn("flex gap-2", "[&>*]:min-w-0 [&>*]:flex-1", className)}
      {...props}
    />
  ),
);
MetricRow.displayName = "MetricRow";

export { Metric, MetricRow };
